import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/auth-context';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Loader2, RefreshCw, Ghost, Wrench } from 'lucide-react';
import { useOnboardingTracking } from '@/hooks/useOnboardingTracking';
import { useToast } from '@/components/ui/use-toast';
import { agentService } from '@/lib/api/agent-service';
import type { Agent, AgentStatsSummary } from '@/types/agent-types-new';
import { AgentCard } from '@/components/agents/AgentCard';
import { AgentFilters, type AgentFilterState } from '@/components/agents/AgentFilters';
import { AgentProfileDrawer, type AgentProfile } from '@/components/agents/AgentProfileDrawer';

/**
 * /agents: the agents table as recorded (GET /api/agents) with execution statistics
 * measured from agent_logs (GET /api/logs/stats/summary). Nothing on this page is
 * estimated: a field the row or the logs do not carry is shown as not recorded, and a
 * request that fails is shown as the error it was (task-dqgtp8n3o8, PR #6308 round 3).
 *
 * Category: the agents table carries no category column; the label in the static
 * classification is product copy, not a recorded field, so the category filter stays
 * hidden (acceptance criterion 1).
 * Promote: no maturity is persisted anywhere (maturity is the same static
 * classification), so there is no Promote control (acceptance criterion 2).
 */
const AgentsPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { trackOnboardingStep } = useOnboardingTracking();
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<AgentFilterState['status']>('all');
  const [sortBy, setSortBy] = useState<AgentFilterState['sortBy']>('name');
  const [activeTab, setActiveTab] = useState('all');
  const [statusPending, setStatusPending] = useState(false);

  useEffect(() => {
    trackOnboardingStep('meet_crew', { timestamp: new Date().toISOString() });
  }, [trackOnboardingStep]);

  const { data: agents, isLoading, isError, error, refetch } = useQuery({
    enabled: isAuthenticated,
    queryKey: ['agents'],
    queryFn: () => agentService.getAgents(true, false),
  });

  const { data: stats, error: statsError, refetch: refetchStats, isFetching: statsFetching } = useQuery({
    enabled: isAuthenticated,
    queryKey: ['agent-stats-summary'],
    queryFn: () => agentService.getAgentStatsSummary(),
  });
  // Refresh re-reads both the rows and the measured statistics, so a failed summary
  // can be retried from the same control and the counts never outlive the list.
  const refreshAll = () => Promise.all([refetch(), refetchStats()]);

  // The drawer shows the agent as the list currently holds it, so a status the API
  // accepted is visible only once the list has been re-read.
  const selectedAgent = useMemo(() => agents?.find((a) => a._id === selectedId) ?? null, [agents, selectedId]);

  const handleViewProfile = (agent: Agent) => {
    setSelectedId(agent._id);
    setIsProfileDrawerOpen(true);
  };

  const handleStatusChange = async (agentId: string, status: 'active' | 'paused') => {
    setStatusPending(true);
    try {
      await agentService.updateAgentStatus(agentId, status);
      const reload = await refetch();
      if (reload.error) {
        toast({ title: 'Status saved, but reload failed', description: reload.error instanceof Error ? reload.error.message : String(reload.error), variant: 'destructive' });
        return;
      }
      toast({ title: 'Status updated', description: `Recorded status is now ${status}.` });
    } catch (e) {
      toast({ title: 'Status not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    } finally {
      setStatusPending(false);
    }
  };

  // AgentFilters owns one AgentFilterState; this page keeps search/status/sort
  // separately. Its 'inactive' means 'paused' here; 'error' matches nothing recorded.
  const filters: AgentFilterState = { search: searchQuery, status: statusFilter, category: 'all', sortBy };
  const handleFilterChange = (next: AgentFilterState) => {
    setSearchQuery(next.search);
    setStatusFilter(next.status);
    setSortBy(next.sortBy);
  };

  // Joined by the RECORDED type (agent_logs.agent_type), never the canonical alias.
  // After a failed refresh React Query keeps the previous data; the alert says the cards
  // show none, so none are shown rather than a cached value passed off as current.
  const statsFor = (agent: Agent): AgentStatsSummary | null => {
    if (!stats || statsError) return null;
    return stats[agent.agent_type ?? agent._id] ?? stats[agent._id] ?? null;
  };

  // The card shows the recorded status word and only measured metrics.
  const toCardAgent = (agent: Agent) => {
    const s = statsFor(agent);
    const rate = agentService.successRateOf(s);
    const metrics = s ? {
      requests: s.executions,
      avgResponseTime: s.avgDurationMs === null ? undefined : Math.round(s.avgDurationMs),
      successRate: rate === null ? undefined : Math.round(rate * 10) / 10, // over terminal rows only
    } : undefined;
    return {
      id: agent._id,
      name: agent.name,
      description: agent.mission,
      status: agent.status,
      type: agent.agent_type ?? agent._id,
      lastActive: s?.lastAt ? new Date(s.lastAt).toLocaleString() : undefined,
      metrics,
    };
  };

  const toProfile = (agent: Agent): AgentProfile => ({
    id: agent._id,
    name: agent.name,
    type: agent.agent_type ?? agent._id,
    description: agent.mission,
    status: agent.status,
  });

  const sortAgents = (list: Agent[]) => [...list].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'status') return a.status.localeCompare(b.status);
    // Both branches are unchanged; only the names now match what they read (task-er6cn4n84j).
    if (sortBy === 'lastExecution') return (statsFor(b)?.lastAt ?? '').localeCompare(statsFor(a)?.lastAt ?? '');
    if (sortBy === 'executions') return (statsFor(b)?.executions ?? -1) - (statsFor(a)?.executions ?? -1);
    return 0;
  });

  const getFilteredAgents = () => {
    if (!agents) return [];
    let filtered = [...agents];
    if (activeTab === 'active') filtered = filtered.filter((a) => a.status === 'active');
    else if (activeTab === 'paused') filtered = filtered.filter((a) => a.status === 'paused');
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((a) => a.name.toLowerCase().includes(q) || (a.mission ?? '').toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((a) => (statusFilter === 'inactive' ? a.status === 'paused' : a.status === statusFilter));
    }
    return filtered;
  };
  const filteredAgents = sortAgents(getFilteredAgents());

  const grid = (list: Agent[]) => list.length > 0 ? (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {list.map((agent) => (
        <AgentCard key={agent._id} agent={toCardAgent(agent)} onViewDetails={() => handleViewProfile(agent)} />
      ))}
    </div>
  ) : <EmptyStateFiltered />;

  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Agents</h1>
          <div className="text-xs text-muted-foreground mt-1">
            Recorded in the agents table; statistics measured from agent_logs.
            <Link to="/agents/fix-agents" className="ml-2 text-primary hover:underline"><Wrench className="h-3 w-3 inline" /> Connection</Link>
          </div>
        </div>
        <Button variant="outline" onClick={() => refreshAll()} disabled={isLoading || statsFetching || authLoading}>
          {isLoading || statsFetching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {authLoading ? (
        <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin" /><span className="ml-2">Authenticating...</span></div>
      ) : isLoading ? (
        <div className="flex justify-center items-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : isError ? (
        <div role="alert" className="text-center p-4 border border-red-200 rounded-md bg-red-50">
          <div className="text-red-600 font-semibold mb-2">Agents could not be loaded</div>
          <p className="text-sm mb-4 break-all">{error instanceof Error ? error.message : String(error)}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Button onClick={() => refreshAll()} variant="outline"><RefreshCw className="h-4 w-4 mr-2" />Retry</Button>
            <Link to="/agents/fix-agents"><Button variant="default"><Wrench className="h-4 w-4 mr-2" />Troubleshoot Connection</Button></Link>
          </div>
        </div>
      ) : !agents || agents.length === 0 ? (
        <EmptyStateNoAgents />
      ) : (
        <>
          {statsError && (
            <div role="alert" className="mb-4 p-3 text-sm border border-amber-200 rounded-md bg-amber-50 text-amber-900">
              Execution statistics could not be loaded, so the cards show none: {statsError instanceof Error ? statsError.message : String(statsError)}
            </div>
          )}
          <AgentFilters filters={filters} onFilterChange={handleFilterChange} showCategory={false} />
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="mt-6">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="paused">Paused</TabsTrigger>
            </TabsList>
            <TabsContent value="all">{grid(filteredAgents)}</TabsContent>
            <TabsContent value="active">{grid(filteredAgents)}</TabsContent>
            <TabsContent value="paused">{grid(filteredAgents)}</TabsContent>
          </Tabs>
        </>
      )}

      <AgentProfileDrawer
        agent={selectedAgent ? toProfile(selectedAgent) : null}
        stats={selectedAgent ? statsFor(selectedAgent) : null}
        statsError={statsError instanceof Error ? statsError.message : statsError ? String(statsError) : null}
        statsLoading={statsFetching}
        open={isProfileDrawerOpen}
        onOpenChange={setIsProfileDrawerOpen}
        onStatusChange={handleStatusChange}
        statusPending={statusPending}
      />
    </div>
  );
};

const EmptyStateNoAgents = () => (
  <Card className="p-8 text-center">
    <div className="mx-auto mb-4 w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center"><Ghost className="h-10 w-10 text-primary" /></div>
    <h3 className="text-2xl font-bold mb-3">No agents recorded</h3>
    <p className="text-muted-foreground max-w-md mx-auto">The agents table returned no rows.</p>
  </Card>
);

const EmptyStateFiltered = () => (
  <Card className="p-6 text-center">
    <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Ghost className="h-6 w-6 text-muted-foreground" /></div>
    <h3 className="text-lg font-medium mb-2">No agents match these filters</h3>
    <p className="text-muted-foreground text-sm">Try adjusting your search or filter criteria</p>
  </Card>
);

export default AgentsPage;
